// Shared pieces for the email library files: links, photo helpers and the block builder.
// Facts come from lavirmedicalspa.com. Lines marked "// verify" need Jen's yes before sending.

import { BOOKING_URL, JEN_PHOTO, newId, stock, type Block, type Img } from '../blocks';
import type { LookId } from '../types';

export { BOOKING_URL, JEN_PHOTO, stock };
export type { Block, Img };

/** One ready-made email. Year emails have a month; library emails have a category instead. */
export interface PlannedEmail {
  key: string; // unique. Year: 2026-/2027-. Welcome: welcome-. Library: lib-<category>-<slug>.
  month: number; // 1-12 for the year plan, 0 for welcome and library emails
  sendDay: number; // year: suggested day of the month. welcome: days after joining. library: 0
  why: string; // one plain line for Jen: when to send it and why
  verify?: string[]; // facts Jen confirms before sending (dates, hours, offers). Shown in the app.
  category?: CategoryId; // library emails only
  look: LookId;
  name: string;
  subject: string;
  preheader: string;
  blocks: Block[];
}

export type CategoryId = 'moments' | 'treatments' | 'askjen' | 'care' | 'news' | 'seasons';

export const CATEGORIES: { id: CategoryId; label: string; hint: string }[] = [
  { id: 'moments', label: 'Big moments', hint: 'Hosting, weddings, reunions, trips, photos. Count back from the date.' },
  { id: 'treatments', label: 'Treatments', hint: 'One treatment explained, start to finish.' },
  { id: 'askjen', label: 'Ask Jen', hint: 'Questions clients ask, answered by a nurse.' },
  { id: 'care', label: 'Client care', hint: 'Aftercare, rebooking, birthdays, thank yous, referrals.' },
  { id: 'news', label: 'Spa news', hint: 'Openings, hours, events and new treatments.' },
  { id: 'seasons', label: 'Seasons', hint: 'Extra seasonal emails beyond your year.' },
];

export const SITE = 'https://www.lavirmedicalspa.com';
export const GIFT = SITE; // no gift card page on the site yet; Jen can swap in a direct link
export const CONTACT = `${SITE}/contact-us`;
export const URL = {
  aerolase: `${SITE}/aerolase-neo-elite-treatments`,
  botox: `${SITE}/anti-wrinkle-injections`,
  filler: `${SITE}/dermal-fillers`,
  hydrafacial: `${SITE}/hydrafacial`,
  vipeel: `${SITE}/vi-peel`,
  kybella: `${SITE}/kybella`,
  iv: `${SITE}/iv-hydration`,
  microneedling: `${SITE}/microneedling-exosome-therapy`,
  hair: `${SITE}/hair-restoration`,
  shots: `${SITE}/vitamin-shots`,
  dermaplaning: `${SITE}/dermaplaning`,
  diamondglow: `${SITE}/diamond-glow`,
  pdo: `${SITE}/pdo`,
  sclero: `${SITE}/sclerotherapy`,
  skinvive: `${SITE}/skinvive-by-juvederm`,
};

export const JEN = 'Jen Tancinco, RN, BSN, Nurse Injector';

/** Builds a block with a fresh id. Call only inside a build function, never at load time. */
export function b<T extends Block['type']>(block: Omit<Extract<Block, { type: T }>, 'id'> & { type: T }): Block {
  return { id: newId(), ...block } as Block;
}

/** Wide crop (1200x800) for hero and full-width photo blocks. */
export const wide = (base: string, alt: string): Img => stock(`year/${base}-w.jpg`, alt);
/** Tall crop (800x1000) for photo + text and two-photo blocks. */
export const tall = (base: string, alt: string): Img => stock(`year/${base}-t.jpg`, alt, 800, 1000);
